import React, { useRef, useState, useEffect } from 'react';

export default function NDASignaturePage({ onComplete }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSigned(true);
  };

  const stopDraw = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const handleSubmit = () => {
    if (!hasSigned || !agreed) return;
    onComplete();
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: 20
    }}>
      <div style={{
        width: '100%', maxWidth: 640, padding: 36, background: 'var(--card)',
        borderRadius: 16, border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>
            Non-Disclosure Agreement
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
            Miskulin Technologies &mdash; Confidentiality &amp; IP Protection
          </p>
        </div>

        <div style={{
          maxHeight: 220, overflowY: 'auto', padding: 20, background: 'var(--bg)',
          borderRadius: 10, border: '1px solid var(--border)', marginBottom: 20,
          fontSize: 12, lineHeight: 1.7, color: 'var(--text-secondary)'
        }}>
          <p style={{ fontWeight: 700, color: 'var(--text)', marginTop: 0 }}>
            CONFIDENTIALITY AND NON-DISCLOSURE AGREEMENT
          </p>
          <p>
            This Non-Disclosure Agreement ("Agreement") is entered into by and between
            Miskulin Technologies, a Canadian corporation with its principal office at
            121 Rainbow Falls Bay, Chestermere, AB T1X 0S5, Canada ("Company"), and the undersigned
            individual ("Recipient").
          </p>
          <p style={{ fontWeight: 600, color: 'var(--text)' }}>1. Confidential Information</p>
          <p>
            "Confidential Information" includes all proprietary data, trade secrets,
            business strategies, software source code, algorithms, customer lists,
            financial records, technical documentation, product roadmaps, and any
            information designated as confidential by the Company. This includes but
            is not limited to all intellectual property related to the Company's
            software platforms, internal tools, and operational systems.
          </p>
          <p style={{ fontWeight: 600, color: 'var(--text)' }}>2. Non-Disclosure Obligations</p>
          <p>
            The Recipient agrees to: (a) hold all Confidential Information in strict
            confidence; (b) not disclose Confidential Information to any third party
            without prior written consent; (c) use Confidential Information solely for
            purposes authorized by the Company; (d) take all reasonable measures to
            protect the secrecy of the Confidential Information.
          </p>
          <p style={{ fontWeight: 600, color: 'var(--text)' }}>3. Intellectual Property</p>
          <p>
            All work product, inventions, code, designs, and materials created by the
            Recipient during the course of engagement with the Company shall be the
            sole and exclusive property of Miskulin Technologies. The Recipient hereby
            assigns all rights, title, and interest in such work product to the Company.
          </p>
          <p style={{ fontWeight: 600, color: 'var(--text)' }}>4. Non-Compete</p>
          <p>
            The Recipient agrees not to directly compete with the Company's business
            operations or solicit the Company's clients, customers, or team members
            for a period of twelve (12) months following the termination of engagement.
          </p>
          <p style={{ fontWeight: 600, color: 'var(--text)' }}>5. Term and Enforcement</p>
          <p>
            This Agreement shall remain in effect for five (5) years from the date of
            execution. Breach of this Agreement may result in legal action, including
            injunctive relief and monetary damages. This Agreement shall be governed
            by the laws of the Province of Alberta, Canada.
          </p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            Your Signature
          </p>
          <canvas
            ref={canvasRef}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
            style={{
              width: '100%', height: 100, borderRadius: 8,
              border: '1px solid var(--border)', cursor: 'crosshair',
              touchAction: 'none'
            }}
          />
          <button
            onClick={clearSignature}
            style={{
              marginTop: 8, padding: '4px 12px', background: 'transparent',
              border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-muted)',
              fontSize: 11, cursor: 'pointer'
            }}
          >
            Clear Signature
          </button>
        </div>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20,
          cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13
        }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={{ marginTop: 2, accentColor: 'var(--primary)' }}
          />
          <span>
            I have read and agree to the terms of this Non-Disclosure Agreement with
            Miskulin Technologies. I understand that violation of this agreement may
            result in legal action.
          </span>
        </label>

        <button
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={!hasSigned || !agreed}
          style={{
            width: '100%', padding: '12px 0', background: (!hasSigned || !agreed) ? '#333' : 'var(--primary)',
            color: (!hasSigned || !agreed) ? '#666' : '#fff',
            border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
            cursor: (!hasSigned || !agreed) ? 'not-allowed' : 'pointer'
          }}
        >
          I Agree &amp; Sign
        </button>
      </div>
    </div>
  );
}
